// V3 — Automation Impacts catalog.
// Each row is a specific automation that reduces time spent in a scoped
// slice of work: Discipline × Channel × Level × OMCL Sub-Phase. Empty
// selection on any axis means "applies to all" for that axis. The
// Phase × Level hour-concentration card uses this catalog to surface hour
// savings on top of the time-concentration heatmap.
//
// Scope axes:
//   - disciplineChannelKeys — combined list sourced from `model.spendPerFte`,
//     where each key is `${disciplineId}::${channelId}` (channelId may be
//     "all" for channel-agnostic disciplines like Strategy, AdOps, Analytics).
//   - levelIds — model.levels with "L1 · {label}" display
//   - subPhaseIds — OMCL sub-phases (the more specific level; phases are
//     shown only as visual section headers)

import { useMemo, useState } from 'react';
import clsx from 'clsx';
import { OMCL_PHASES, OMCL_SUB_PHASES } from '@rc/engine';
import type {
  AutomationImpactRow,
  LevelId,
  Model,
} from '@rc/engine/types';

interface Props {
  model: Model;
  onChange: (m: Model) => void;
}

const r2 = (n: number) => Math.round(n * 100) / 100;
const DC_SEP = '::';
const dcKey = (disciplineId: string, channelId: string) =>
  `${disciplineId}${DC_SEP}${channelId}`;

function newRowId(existing: AutomationImpactRow[]): string {
  let i = existing.length + 1;
  let candidate = `automation_${i}`;
  const taken = new Set(existing.map((r) => r.id));
  while (taken.has(candidate)) {
    i += 1;
    candidate = `automation_${i}`;
  }
  return candidate;
}

interface DcOption {
  key: string;
  disciplineId: string;
  channelId: string;
  disciplineLabel: string;
  /** Display label for the picker, e.g. "Strategy" or "Investment · Programmatic". */
  label: string;
}

/** Build the canonical Discipline × Channel option list from the model's
 *  spendPerFte taxonomy. Falls back to model.disciplines when spendPerFte is
 *  empty so the editor still renders something sensible. */
function buildDcOptions(model: Model): DcOption[] {
  const disciplineLabel = (id: string) =>
    model.disciplines?.find((d) => d.id === id)?.label || id;
  const channelLabel = (id: string) => {
    if (id === 'all') return null;
    return model.channels?.find((c) => c.id === id)?.label || id;
  };

  const rows = model.spendPerFte || [];
  if (rows.length > 0) {
    const seen = new Set<string>();
    const out: DcOption[] = [];
    for (const r of rows) {
      const key = dcKey(r.disciplineId, r.channelId);
      if (seen.has(key)) continue;
      seen.add(key);
      const dl = disciplineLabel(r.disciplineId);
      const cl = channelLabel(r.channelId);
      out.push({
        key,
        disciplineId: r.disciplineId,
        channelId: r.channelId,
        disciplineLabel: dl,
        label: cl ? `${dl} · ${cl}` : dl,
      });
    }
    return out;
  }

  return (model.disciplines || []).map((d) => ({
    key: dcKey(d.id, 'all'),
    disciplineId: d.id,
    channelId: 'all',
    disciplineLabel: d.label,
    label: d.label,
  }));
}

export default function AutomationImpactsEditor({ model, onChange }: Props) {
  const rows = model.automationImpacts || [];
  const dcOptions = useMemo(() => buildDcOptions(model), [model]);
  const [expandedId, setExpandedId] = useState<string | null>(rows[0]?.id ?? null);

  const setRows = (next: AutomationImpactRow[]) =>
    onChange({ ...model, automationImpacts: next });

  const update = (idx: number, patch: Partial<AutomationImpactRow>) => {
    const next = [...rows];
    next[idx] = { ...next[idx], ...patch };
    setRows(next);
  };

  const remove = (idx: number) => {
    const r = rows[idx];
    if (!r) return;
    if (!confirm(`Remove automation "${r.label || r.id}"?`)) return;
    setRows(rows.filter((_, i) => i !== idx));
    if (expandedId === r.id) setExpandedId(null);
  };

  const add = () => {
    const id = newRowId(rows);
    const created: AutomationImpactRow = {
      id,
      label: 'New automation',
      reductionPct: 0.2,
      disciplineChannelKeys: [],
      levelIds: [],
      subPhaseIds: [],
      active: true,
    };
    setRows([...rows, created]);
    setExpandedId(id);
  };

  return (
    <div className="space-y-4">
      <Header onAdd={add} count={rows.length} />

      {rows.length === 0 ? (
        <EmptyState onAdd={add} />
      ) : (
        <div className="space-y-2">
          {rows.map((r, i) => (
            <AutomationCard
              key={r.id}
              row={r}
              model={model}
              dcOptions={dcOptions}
              expanded={expandedId === r.id}
              onToggle={() => setExpandedId((prev) => (prev === r.id ? null : r.id))}
              onChange={(patch) => update(i, patch)}
              onRemove={() => remove(i)}
            />
          ))}
        </div>
      )}

      <p className="text-xs text-slate-400">
        Automations reduce time spent in matching{' '}
        <strong>Discipline × Channel × Level × Sub-phase</strong> cells. Leaving a scope
        empty means the automation applies to <em>all</em> values on that axis. Phase
        scoping is implicit from the parent phase of each selected sub-phase.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function Header({ onAdd, count }: { onAdd: () => void; count: number }) {
  return (
    <div className="flex items-end justify-between gap-4">
      <div>
        <h3 className="text-sm font-semibold text-slate-700">Automation impact catalog</h3>
        <p className="text-xs text-slate-500 mt-0.5">
          {count} automation{count === 1 ? '' : 's'} configured. Each entry applies a
          % time reduction to a scoped slice of work.
        </p>
      </div>
      <button className="btn-secondary text-xs" onClick={onAdd}>
        + Add automation
      </button>
    </div>
  );
}

function EmptyState({ onAdd }: { onAdd: () => void }) {
  return (
    <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50/40 p-8 text-center">
      <p className="text-sm text-slate-600">No automations configured yet.</p>
      <p className="text-xs text-slate-400 mt-1">
        Add specific automations (e.g. "AI Brief Generator", "Auto-Tagging Pipeline") and
        scope them to the disciplines, channels, levels, and sub-phases they impact.
      </p>
      <button className="btn-primary text-sm mt-4" onClick={onAdd}>
        + Add first automation
      </button>
    </div>
  );
}

function AutomationCard({
  row,
  model,
  dcOptions,
  expanded,
  onToggle,
  onChange,
  onRemove,
}: {
  row: AutomationImpactRow;
  model: Model;
  dcOptions: DcOption[];
  expanded: boolean;
  onToggle: () => void;
  onChange: (patch: Partial<AutomationImpactRow>) => void;
  onRemove: () => void;
}) {
  const isActive = row.active !== false;

  return (
    <div
      className={clsx(
        'rounded-lg border bg-white shadow-sm transition-all',
        isActive ? 'border-slate-200' : 'border-slate-200/60 bg-slate-50/40',
      )}
    >
      {/* Header row — always visible */}
      <div className="flex items-center gap-3 px-4 py-3">
        <input
          type="checkbox"
          checked={isActive}
          onChange={(e) => onChange({ active: e.target.checked })}
          title={isActive ? 'Active' : 'Inactive'}
          className="shrink-0"
        />

        <input
          className={clsx(
            'input py-1 px-2 text-sm font-medium flex-1 min-w-0',
            !isActive && 'text-slate-500',
          )}
          value={row.label}
          onChange={(e) => onChange({ label: e.target.value })}
          placeholder="Automation name"
        />

        <ReductionInput
          value={row.reductionPct}
          onChange={(v) => onChange({ reductionPct: v })}
          disabled={!isActive}
        />

        <ScopeSummary row={row} model={model} dcOptions={dcOptions} />

        <button
          className="btn-ghost text-xs whitespace-nowrap"
          onClick={onToggle}
          aria-expanded={expanded}
        >
          {expanded ? 'Hide details' : 'Edit scope'}
        </button>
        <button
          className="btn-ghost text-xs text-rose-600 hover:bg-rose-50"
          onClick={onRemove}
          title="Remove automation"
        >
          Remove
        </button>
      </div>

      {/* Expanded details */}
      {expanded && (
        <div className="border-t border-slate-100 px-4 py-4 space-y-4 bg-slate-50/30">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
            <Field label="ID">
              <input
                className="input py-1 px-2 text-xs font-mono w-full"
                value={row.id}
                onChange={(e) => onChange({ id: e.target.value })}
              />
            </Field>
            <Field label="Description" className="lg:col-span-2">
              <input
                className="input py-1 px-2 text-sm w-full"
                value={row.description || ''}
                onChange={(e) => onChange({ description: e.target.value })}
                placeholder="What does this automation do? (optional)"
              />
            </Field>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <DisciplineChannelScope
              dcOptions={dcOptions}
              selected={row.disciplineChannelKeys}
              onChange={(selected) => onChange({ disciplineChannelKeys: selected })}
            />
            <ScopeBlock
              title="Levels"
              emptyLabel="All levels"
              options={model.levels.map((l) => ({
                id: l.id,
                label: `${l.id} · ${l.label}`,
              }))}
              selected={row.levelIds}
              onChange={(selected) =>
                onChange({ levelIds: selected as LevelId[] })
              }
            />
          </div>

          <SubPhaseScope
            row={row}
            onChange={(selected) => onChange({ subPhaseIds: selected })}
          />
        </div>
      )}
    </div>
  );
}

function ReductionInput({
  value,
  onChange,
  disabled,
}: {
  value: number;
  onChange: (v: number) => void;
  disabled?: boolean;
}) {
  const pct = r2((value || 0) * 100);
  return (
    <div
      className={clsx(
        'flex items-center gap-1 shrink-0',
        disabled && 'opacity-50',
      )}
      title="Time-spent reduction in matching cells"
    >
      <input
        type="number"
        min={0}
        max={100}
        step={1}
        className="input py-1 px-2 text-sm w-20 text-right"
        value={pct}
        onChange={(e) => {
          const next = Math.max(0, Math.min(100, Number(e.target.value) || 0));
          onChange(next / 100);
        }}
        disabled={disabled}
      />
      <span className="text-xs text-slate-500">% saved</span>
    </div>
  );
}

function ScopeSummary({
  row,
  model,
  dcOptions,
}: {
  row: AutomationImpactRow;
  model: Model;
  dcOptions: DcOption[];
}) {
  const dc = row.disciplineChannelKeys.length;
  const lvl = row.levelIds.length;
  const sub = row.subPhaseIds?.length || 0;
  const totalDc = dcOptions.length;
  const totalLvl = model.levels.length;
  const totalSub = OMCL_SUB_PHASES.length;

  const parts: { key: string; text: string; tone: 'all' | 'scoped' }[] = [
    {
      key: 'dc',
      text: dc === 0 ? `All ${totalDc} D×C` : `${dc} D×C`,
      tone: dc === 0 ? 'all' : 'scoped',
    },
    {
      key: 'l',
      text: lvl === 0 ? `All ${totalLvl} levels` : `${lvl} levels`,
      tone: lvl === 0 ? 'all' : 'scoped',
    },
    {
      key: 's',
      text: sub === 0 ? `All ${totalSub} sub-phases` : `${sub} sub-phases`,
      tone: sub === 0 ? 'all' : 'scoped',
    },
  ];

  return (
    <div className="hidden md:flex items-center gap-1 shrink-0">
      {parts.map((p) => (
        <span
          key={p.key}
          className={clsx(
            'px-2 py-0.5 rounded-full text-xs whitespace-nowrap',
            p.tone === 'all'
              ? 'bg-slate-100 text-slate-500'
              : 'bg-brand-purple-50 text-brand-purple-700',
          )}
        >
          {p.text}
        </span>
      ))}
    </div>
  );
}

function Field({
  label,
  children,
  className,
}: {
  label: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <label className={clsx('block', className)}>
      <span className="block text-[11px] uppercase tracking-wider text-slate-500 mb-1">
        {label}
      </span>
      {children}
    </label>
  );
}

function ScopeBlock({
  title,
  emptyLabel,
  options,
  selected,
  onChange,
}: {
  title: string;
  emptyLabel: string;
  options: { id: string; label: string }[];
  selected: string[];
  onChange: (selected: string[]) => void;
}) {
  const allSelected = selected.length === 0;
  const toggle = (id: string) => {
    if (selected.includes(id)) {
      onChange(selected.filter((s) => s !== id));
    } else {
      onChange([...selected, id]);
    }
  };

  return (
    <div className="rounded-md border border-slate-200 bg-white">
      <div className="flex items-center justify-between px-3 py-2 border-b border-slate-100">
        <span className="text-xs font-semibold text-slate-700">{title}</span>
        <button
          type="button"
          className={clsx(
            'text-[11px] px-2 py-0.5 rounded-full transition-colors',
            allSelected
              ? 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200'
              : 'bg-slate-100 text-slate-600 hover:bg-slate-200',
          )}
          onClick={() => onChange([])}
          title="Clear selection (applies to all)"
        >
          {allSelected ? `✓ ${emptyLabel}` : 'Reset to all'}
        </button>
      </div>
      <div className="max-h-44 overflow-y-auto px-3 py-2 space-y-1">
        {options.map((o) => {
          const checked = selected.includes(o.id);
          return (
            <label
              key={o.id}
              className={clsx(
                'flex items-center gap-2 text-xs cursor-pointer rounded px-1 py-0.5',
                checked && 'bg-brand-purple-50',
              )}
            >
              <input
                type="checkbox"
                checked={checked}
                onChange={() => toggle(o.id)}
              />
              <span className={clsx(checked ? 'text-slate-800' : 'text-slate-600')}>
                {o.label}
              </span>
            </label>
          );
        })}
      </div>
    </div>
  );
}

function DisciplineChannelScope({
  dcOptions,
  selected,
  onChange,
}: {
  dcOptions: DcOption[];
  selected: string[];
  onChange: (selected: string[]) => void;
}) {
  // Group options by discipline so the picker stays scannable when a
  // discipline (e.g. Investment) has many channels.
  const groups = useMemo(() => {
    const byDisc = new Map<string, { disciplineId: string; disciplineLabel: string; opts: DcOption[] }>();
    for (const o of dcOptions) {
      const g = byDisc.get(o.disciplineId);
      if (g) g.opts.push(o);
      else byDisc.set(o.disciplineId, {
        disciplineId: o.disciplineId,
        disciplineLabel: o.disciplineLabel,
        opts: [o],
      });
    }
    return [...byDisc.values()];
  }, [dcOptions]);

  const allSelected = selected.length === 0;
  const selectedSet = new Set(selected);

  const toggle = (key: string) => {
    if (selectedSet.has(key)) {
      onChange(selected.filter((s) => s !== key));
    } else {
      onChange([...selected, key]);
    }
  };

  const toggleDiscipline = (disciplineId: string) => {
    const inDisc = dcOptions.filter((o) => o.disciplineId === disciplineId).map((o) => o.key);
    const allInDiscSelected = inDisc.every((k) => selectedSet.has(k));
    if (allInDiscSelected) {
      onChange(selected.filter((s) => !inDisc.includes(s)));
    } else {
      const next = new Set(selected);
      inDisc.forEach((k) => next.add(k));
      onChange([...next]);
    }
  };

  return (
    <div className="rounded-md border border-slate-200 bg-white">
      <div className="flex items-center justify-between px-3 py-2 border-b border-slate-100">
        <span className="text-xs font-semibold text-slate-700">Discipline × Channel</span>
        <button
          type="button"
          className={clsx(
            'text-[11px] px-2 py-0.5 rounded-full transition-colors',
            allSelected
              ? 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200'
              : 'bg-slate-100 text-slate-600 hover:bg-slate-200',
          )}
          onClick={() => onChange([])}
          title="Clear selection (applies to all)"
        >
          {allSelected ? `✓ All ${dcOptions.length} pairs` : 'Reset to all'}
        </button>
      </div>
      <div className="max-h-72 overflow-y-auto px-3 py-2 space-y-2">
        {groups.map((g) => {
          const inDisc = g.opts.map((o) => o.key);
          const allInDisc = inDisc.every((k) => selectedSet.has(k));
          const someInDisc = !allInDisc && inDisc.some((k) => selectedSet.has(k));
          // Single-option group (e.g. Strategy → "Strategy"): treat the option itself
          // as the row, no need for the group header / sub-list.
          if (g.opts.length === 1) {
            const o = g.opts[0]!;
            const checked = selectedSet.has(o.key);
            return (
              <label
                key={g.disciplineId}
                className={clsx(
                  'flex items-center gap-2 text-xs cursor-pointer rounded px-1 py-0.5',
                  checked && 'bg-brand-purple-50',
                )}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggle(o.key)}
                />
                <span className={clsx(checked ? 'text-slate-800' : 'text-slate-600')}>
                  {o.label}
                </span>
              </label>
            );
          }
          return (
            <div key={g.disciplineId}>
              <button
                type="button"
                className={clsx(
                  'w-full flex items-center justify-between gap-2 text-[11px] uppercase tracking-wider px-1 py-0.5 rounded hover:bg-slate-50',
                  allInDisc
                    ? 'text-brand-purple-700'
                    : someInDisc
                      ? 'text-brand-purple-600'
                      : 'text-slate-500',
                )}
                onClick={() => toggleDiscipline(g.disciplineId)}
                title={allInDisc ? 'Clear all channels' : 'Select all channels'}
              >
                <span>{g.disciplineLabel}</span>
                <span className="text-[10px] font-normal normal-case opacity-70">
                  {allInDisc
                    ? `all ${g.opts.length} channels`
                    : someInDisc
                      ? `${g.opts.filter((o) => selectedSet.has(o.key)).length}/${g.opts.length}`
                      : `${g.opts.length} channels`}
                </span>
              </button>
              <div className="mt-0.5 ml-3 space-y-0.5 border-l border-slate-100 pl-2">
                {g.opts.map((o) => {
                  const checked = selectedSet.has(o.key);
                  // Strip the "Discipline · " prefix in the group view.
                  const channelOnlyLabel =
                    o.channelId === 'all'
                      ? 'All channels'
                      : o.label.split(' · ').slice(1).join(' · ') || o.label;
                  return (
                    <label
                      key={o.key}
                      className={clsx(
                        'flex items-center gap-2 text-xs cursor-pointer rounded px-1 py-0.5',
                        checked && 'bg-brand-purple-50',
                      )}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggle(o.key)}
                      />
                      <span className={clsx(checked ? 'text-slate-800' : 'text-slate-600')}>
                        {channelOnlyLabel}
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>
          );
        })}
        {groups.length === 0 && (
          <div className="text-xs text-slate-400 italic">
            No disciplines configured in the model.
          </div>
        )}
      </div>
    </div>
  );
}

function SubPhaseScope({
  row,
  onChange,
}: {
  row: AutomationImpactRow;
  onChange: (selected: string[]) => void;
}) {
  // Sub-phases grouped by their parent phase. Phase rows are non-selectable
  // section headers — selection is sub-phase-only.
  const groups = useMemo(
    () =>
      OMCL_PHASES.map((p) => ({
        phase: p,
        subs: OMCL_SUB_PHASES.filter((s) => s.phaseId === p.id),
      })),
    [],
  );

  const selected = new Set(row.subPhaseIds || []);
  const totalAvailable = OMCL_SUB_PHASES.length;
  const allSelected = selected.size === 0;

  const toggle = (id: string) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onChange([...next]);
  };

  const togglePhase = (phaseId: string) => {
    const inPhase = OMCL_SUB_PHASES.filter((s) => s.phaseId === phaseId).map(
      (s) => s.id,
    );
    const allInPhase = inPhase.every((id) => selected.has(id));
    const next = new Set(selected);
    if (allInPhase) {
      inPhase.forEach((id) => next.delete(id));
    } else {
      inPhase.forEach((id) => next.add(id));
    }
    onChange([...next]);
  };

  return (
    <div className="rounded-md border border-slate-200 bg-white">
      <div className="flex items-center justify-between px-3 py-2 border-b border-slate-100">
        <div>
          <span className="text-xs font-semibold text-slate-700">Sub-phases</span>
          <span className="text-[11px] text-slate-400 ml-2">
            grouped by OMCL phase — select the specific stages this automation impacts
          </span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="text-[11px] text-slate-500">
            {allSelected
              ? `All ${totalAvailable} sub-phases`
              : `${selected.size} of ${totalAvailable}`}
          </span>
          {!allSelected && (
            <button
              type="button"
              className="text-[11px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 hover:bg-slate-200"
              onClick={() => onChange([])}
            >
              Reset
            </button>
          )}
        </div>
      </div>
      <div className="px-3 py-2 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-3">
        {groups.map((g) => {
          const inPhase = g.subs.map((s) => s.id);
          const allInPhase = inPhase.every((id) => selected.has(id));
          const someInPhase = !allInPhase && inPhase.some((id) => selected.has(id));
          return (
            <div key={g.phase.id}>
              <button
                type="button"
                className={clsx(
                  'w-full flex items-center justify-between gap-2 text-[11px] uppercase tracking-wider px-1 py-0.5 rounded hover:bg-slate-50',
                  allInPhase
                    ? 'text-brand-purple-700'
                    : someInPhase
                      ? 'text-brand-purple-600'
                      : 'text-slate-500',
                )}
                onClick={() => togglePhase(g.phase.id)}
                title={allInPhase ? 'Clear phase sub-phases' : 'Select all sub-phases in phase'}
              >
                <span>{g.phase.label}</span>
                <span className="text-[10px] font-normal normal-case opacity-70">
                  {allInPhase
                    ? `all ${g.subs.length}`
                    : someInPhase
                      ? `${g.subs.filter((s) => selected.has(s.id)).length}/${g.subs.length}`
                      : `${g.subs.length} sub-phases`}
                </span>
              </button>
              <div className="mt-0.5 space-y-0.5">
                {g.subs.map((s) => {
                  const checked = selected.has(s.id);
                  return (
                    <label
                      key={s.id}
                      className={clsx(
                        'flex items-center gap-2 text-xs cursor-pointer rounded px-1 py-0.5',
                        checked && 'bg-brand-purple-50',
                      )}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggle(s.id)}
                      />
                      <span className={clsx(checked ? 'text-slate-800' : 'text-slate-600')}>
                        {s.label}
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
